import {
  ALL_PAGES,
  countBy,
  getPageKey,
  getPageNameLabel,
  groupOfRow,
  MyReportsScope,
} from './myReportsListHelpers';
import { MyReport } from './MyReportsTable.types';
import { SavedReportsList } from '../../../@types';
import { QuickToggleOption } from '../../../components/molecules/QuickToggleGroup';

export interface OptionItem {
  label: string;
  value: string;
}

export const collectOwnerOptions = (rows: SavedReportsList[]): OptionItem[] => {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const owner = row.reportOwner;
    if (owner?.id == null) continue;
    const key = String(owner.id);
    if (seen.has(key)) continue;
    seen.set(key, owner.name ?? String(owner.id));
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * One toggle per page the user actually has reports on, plus All.
 *
 * `allRows` fixes which pages exist so the toggles don't appear and disappear as
 * the user filters — a control that reflows under your cursor is unusable.
 * `countedRows` is the same set minus every filter except the page itself, so
 * each count states what clicking it would yield.
 */
export const buildPageToggleOptions = (
  allRows: MyReport[],
  countedRows: MyReport[]
): QuickToggleOption[] => {
  const counts = countBy(countedRows, getPageKey);
  const labels = new Map<string, string>();
  for (const row of allRows) {
    const key = getPageKey(row);
    if (key && !labels.has(key)) labels.set(key, getPageNameLabel(key));
  }

  const pages = Array.from(labels.entries())
    .map(([value, label]) => ({ value, label, count: counts.get(value) ?? 0 }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [{ value: ALL_PAGES, label: 'All pages', count: countedRows.length }, ...pages];
};

const SCOPE_LABELS: { value: MyReportsScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'My Reports' },
  { value: 'shared', label: 'Shared With Me' },
  { value: 'other', label: 'Other' },
];

/**
 * The group toggles. "Other" is internal-only — client/customer users never see
 * that group, so offering them a toggle for it would be a dead control.
 */
export const buildScopeToggleOptions = (
  countedRows: MyReport[],
  mySiblingId: number | undefined,
  isClient: boolean
): QuickToggleOption[] => {
  const counts = countBy(countedRows, (row) => groupOfRow(row, mySiblingId));
  return SCOPE_LABELS.filter((s) => !(isClient && s.value === 'other')).map(({ value, label }) => ({
    value,
    label,
    count: value === 'all' ? countedRows.length : (counts.get(value) ?? 0),
  }));
};
